/**
 * Integrytas Group - Common UI
 *
 * Registry of CHILD EDITORS for the related-children drill-down.
 *
 * A list row that is the parent of other records shows its children in
 * <RelatedChildrenPanel>. Clicking a child opens that child's OWN edit form —
 * the same modal its own page uses — so the drill-down is the real editor and
 * not a second, drifting rendering of the same entity.
 *
 * A parent page cannot import forty other pages' modals: the coupling would be
 * unmaintainable and would cycle (Users -> Roles -> Users). So editors register
 * themselves by entity key, exactly as registerUdcHost does, and the panel
 * resolves them at click time.
 *
 * Registration is a module-load side effect. src/lib/child_editors_registry.ts
 * imports every editor module once, and main.tsx imports that — so the registry
 * is populated before the tree mounts, and a page that is never opened still
 * contributes its editor.
 *
 * PERMISSIONS. The verb is checked against the CHILD's page, not the parent's:
 * opening a Role from the Users page asks for can_change on the ROLE page. A
 * user without it still gets the form — rendered read-only — because seeing the
 * record is the point of a drill-down. Enforcement is the server's (RLS via the
 * Common Routine); this only decides whether the form is editable.
 */
import { createContext, useContext, type ComponentType } from 'react';
import type { AuthSession } from '../types/auth';

export interface ChildEditorProps {
  session: AuthSession;
  /** Primary key of the child row to open. */
  pk: string | number;
  /** True when the caller lacks can_change on this child's page. */
  readOnly: boolean;
  onClose: () => void;
  onSaved: () => void;
}

export interface ChildEditorEntry {
  /** The CHILD's page internal_code — the RBAC subject for this editor. */
  pageCode: string;
  /** Modal title, resolved by the caller for the active locale. */
  title: string;
  Editor: ComponentType<ChildEditorProps>;
}

const REGISTRY = new Map<string, ChildEditorEntry>();

/**
 * Register the editor for a CHILD TABLE, e.g. 'csm_role_master'.
 *
 * Keyed by table because that is what a RelatedChildGroup already declares for
 * the generic fallback — so a group wired for the fallback is upgraded to the
 * real editor the moment its owning page registers one, with no change at the
 * call site.
 *
 * Idempotent: re-registering replaces rather than throws, so hot reload and a
 * double module evaluation do not wedge.
 */
export function registerChildEditor(table: string, entry: ChildEditorEntry): void {
  REGISTRY.set(table, entry);
}

export function getChildEditor(table: string | undefined): ChildEditorEntry | null {
  if (!table) return null;
  return REGISTRY.get(table) ?? null;
}

/** Entities that currently have an editor — used by the coverage check. */
export function registeredChildEntities(): string[] {
  return [...REGISTRY.keys()].sort();
}

/**
 * Renders form controls inert without every editor needing a readOnly prop.
 * A disabled <fieldset> disables all descendant form controls natively, so a
 * modal written with no notion of read-only becomes view-only for free — which
 * is what makes "no access -> same form, view only" affordable across dozens of
 * editors. The reset styles stop the fieldset disturbing the form's layout.
 */
export function ReadOnlyFormGuard({ active, children }: { active: boolean; children: React.ReactNode }) {
  if (!active) return <>{children}</>;
  return (
    <fieldset disabled className="m-0 min-w-0 border-0 p-0" style={{ display: 'contents' }}>
      {children}
    </fieldset>
  );
}

/**
 * Lets a registered editor know it is being rendered read-only, so it can also
 * hide its Save action (a disabled fieldset makes controls inert but a Save
 * button outside the fieldset would still look actionable).
 */
const ReadOnlyCtx = createContext(false);
export const ChildEditorReadOnlyProvider = ReadOnlyCtx.Provider;
export function useChildEditorReadOnly(): boolean {
  return useContext(ReadOnlyCtx);
}
